import React, { useState } from 'react';

export const caseStudiesData = [
  {
    slug: 'ai-support-chatbot',
    title: 'AI-Powered Support Chatbot',
    category: 'Chatbot',
    icon: 'fa-solid fa-comments',
    summary: 'An LLM-powered support chatbot that resolves the majority of customer queries instantly.',
    challenge: 'A growing SaaS company was struggling with support ticket volume overwhelming their small team, pushing response times past 24 hours.',
    solution: 'We designed and deployed an LLM-powered chatbot integrated with their knowledge base and ticketing system, using retrieval-augmented generation to keep answers grounded in accurate product documentation.',
    results: [
      'Cut average first-response time from 24 hours to under 2 minutes',
      'Automatically resolved 60% of incoming tickets without human intervention',
      'Freed the support team to focus on complex escalations',
    ],
    technologies: ['OpenAI', 'LangChain', 'Vector DB', 'Python', 'React'],
  },
  {
    slug: 'large-scale-data-pipeline',
    title: 'Large-Scale Data Engineering Pipeline',
    category: 'Data Engineering',
    icon: 'fa-solid fa-database',
    summary: 'A modern ELT pipeline that turned a 10-hour nightly batch job into near real-time reporting.',
    challenge: 'A retail analytics client had years of transactional data scattered across siloed systems, making reporting slow and unreliable, with nightly batch jobs taking over 10 hours.',
    solution: 'We designed and built a modern ELT pipeline on a cloud data lakehouse, orchestrating ingestion, transformation, and quality checks across billions of rows.',
    results: [
      'Reduced pipeline runtime from 10 hours to under 45 minutes',
      'Enabled near real-time dashboards for business teams',
      'Established a single source of truth for reporting across the company',
    ],
    technologies: ['Databricks', 'Spark', 'Airflow', 'Snowflake', 'AWS'],
  },
  {
    slug: 'agentic-ai-workflow-automation',
    title: 'Agentic AI Workflow Automation',
    category: 'Agentic AI',
    icon: 'fa-solid fa-robot',
    summary: 'A multi-agent system that automates a compliance research workflow analysts used to do by hand.',
    challenge: 'A financial services firm needed to automate a multi-step research and compliance workflow that previously required analysts to manually cross-reference multiple systems.',
    solution: 'We built a multi-agent system where specialized AI agents handle research, document extraction, compliance checks, and summarization, coordinating through a shared task orchestrator.',
    results: [
      'Reduced manual research time by 70%',
      'Improved consistency and auditability of compliance reviews',
      'Freed analysts to focus on judgment calls rather than data gathering',
    ],
    technologies: ['LangGraph', 'LangChain', 'Python', 'Vector DB', 'AWS'],
  },
  {
    slug: 'devops-cicd-transformation',
    title: 'DevOps Transformation & CI/CD Automation',
    category: 'DevOps',
    icon: 'fa-solid fa-infinity',
    summary: 'Automated CI/CD and infrastructure-as-code that took releases from bi-weekly to multiple times a day.',
    challenge: 'An engineering team was shipping releases manually every two weeks, with frequent deployment failures and no consistent rollback strategy.',
    solution: 'We implemented containerized infrastructure, automated CI/CD pipelines, and infrastructure-as-code across staging and production environments.',
    results: [
      'Increased deployment frequency from bi-weekly to multiple times per day',
      'Cut deployment failures by 80%',
      'Reduced mean time to recovery from hours to minutes',
    ],
    technologies: ['Docker', 'Kubernetes', 'Terraform', 'GitHub Actions', 'AWS'],
  },
  {
    slug: 'startup-mvp-launch',
    title: 'MVP Launch for an Early-Stage Startup',
    category: 'MVP',
    icon: 'fa-solid fa-rocket',
    summary: 'A lean, production-ready MVP shipped in 7 weeks ahead of a funding milestone.',
    challenge: 'A funded startup needed to validate their product idea with real users within a tight 8-week runway before their next funding milestone.',
    solution: 'We assembled a focused on-demand team to design and build a lean, production-ready MVP covering the core user journey, with analytics baked in from day one.',
    results: [
      'Shipped the MVP in 7 weeks',
      'Onboarded the first 500 users within a month of launch',
      'Findings directly informed the roadmap and a successful seed extension',
    ],
    technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
  },
  {
    slug: 'rapid-prototyping',
    title: 'Rapid Prototyping for Product Validation',
    category: 'Prototype',
    icon: 'fa-solid fa-drafting-compass',
    summary: 'A clickable, high-fidelity prototype that validated a new feature before full development began.',
    challenge: 'A product team needed to test a new feature concept with stakeholders and early customers before committing engineering resources to a full build.',
    solution: 'We delivered a clickable, high-fidelity prototype with just enough backend logic to simulate real workflows, enabling realistic user testing within days.',
    results: [
      'Validated the concept with 20+ user interviews in two weeks',
      'Identified critical UX issues before full development began',
      'Saved months of potential rework',
    ],
    technologies: ['React', 'Figma', 'Node.js'],
  },
  {
    slug: 'microservices-migration',
    title: 'Microservices Migration for a Legacy Platform',
    category: 'Microservices App Development',
    icon: 'fa-solid fa-cubes',
    summary: 'Breaking a fragile monolith into independently deployable microservices.',
    challenge: 'A legacy monolithic application had become difficult to scale and slow to update, with a single bug risking downtime across the entire platform.',
    solution: 'We decomposed the monolith into independently deployable microservices with clear domain boundaries, introducing an API gateway and service-to-service communication patterns.',
    results: [
      'Enabled independent scaling of high-traffic services',
      'Reduced deployment risk since teams could ship services independently',
      'Improved overall platform uptime',
    ],
    technologies: ['Node.js', 'Docker', 'Kubernetes', 'REST APIs', 'RabbitMQ'],
  },
  {
    slug: 'cctv-manufacturing-automation',
    title: 'CCTV Automation for a Manufacturing Unit',
    category: 'Computer Vision / IoT',
    icon: 'fa-solid fa-video',
    summary: 'AI-powered CCTV automation that turns manufacturing floor footage into real-time alerts and analytics.',
    challenge: 'A manufacturing client relied on manual CCTV monitoring across their production floor, so safety violations and equipment issues were often caught only after the fact, by reviewing footage after an incident.',
    solution: 'We built a computer vision pipeline that processes live CCTV feeds to detect safety violations, unauthorized zone access, and equipment anomalies, feeding results into a real-time analytical dashboard with instant alerts to floor supervisors.',
    results: [
      'Cut incident detection time from hours to seconds with real-time alerts',
      'Gave plant managers a live analytics dashboard across all production lines',
      'Reduced safety violations through faster intervention',
    ],
    technologies: ['Python', 'OpenCV', 'YOLO', 'AWS', 'React'],
  },
  {
    slug: 'custom-python-software',
    title: 'Custom Software Platform Built with Python',
    category: 'Custom Software Development',
    icon: 'fa-solid fa-code',
    summary: 'A bespoke Python platform that replaced spreadsheets and manual processes with one connected system.',
    challenge: 'A logistics operator was running core parts of their business through a patchwork of spreadsheets and manual emails, causing errors and slowing down operations as the company grew.',
    solution: 'We designed and built a custom Python-based software platform tailored to their exact workflow, automating data entry, approvals, and reporting, and integrating with their existing accounting and logistics tools.',
    results: [
      'Eliminated manual spreadsheet work across core operations',
      'Cut order processing time by more than half',
      'Gave leadership real-time visibility into operations through built-in reporting',
    ],
    technologies: ['Python', 'Django', 'PostgreSQL', 'REST APIs', 'AWS'],
  },
  {
    slug: 'healthcare-domain-automation',
    title: 'Healthcare Workflow Automation Platform',
    category: 'Healthcare Domain Automation',
    icon: 'fa-solid fa-notes-medical',
    summary: 'A HIPAA-compliant automation platform that streamlines patient intake, scheduling, and insurance verification.',
    challenge: 'A multi-clinic healthcare provider was managing patient intake, appointment scheduling, and insurance verification through manual, paper-heavy processes, leading to long wait times and frequent billing errors.',
    solution: 'We built a HIPAA-compliant automation platform that digitizes patient intake, automates appointment scheduling and reminders, and validates insurance eligibility in real time, integrating with their existing EHR system.',
    results: [
      'Reduced patient check-in time from 15 minutes to under 3 minutes',
      'Cut insurance-related billing errors by more than 40%',
      'Freed front-desk staff to focus on patient care instead of paperwork',
    ],
    technologies: ['Python', 'HL7/FHIR', 'PostgreSQL', 'AWS', 'React'],
  },
  {
    slug: 'multitenant-social-content-platform',
    title: 'Multi-Tenant AI Content & Social Media Platform',
    category: 'Multi-Tenant SaaS / Marketing Automation',
    icon: 'fa-solid fa-photo-film',
    summary: 'A multi-tenant SaaS platform that auto-generates marketing images, videos, and copy, then schedules and publishes them across social channels.',
    challenge: "An agency serving dozens of brands needed a constant stream of on-brand social content for each client, but manual content creation and scheduling across accounts couldn't scale with their growing client base.",
    solution: "We built a multi-tenant SaaS platform where each brand gets an isolated workspace. Generative AI creates on-brand images, short-form videos, and copy from simple prompts and brand guidelines, and an automated scheduler publishes content directly to each client's connected social accounts.",
    results: [
      'Cut content production time per campaign from days to minutes',
      'Scaled from a handful of brands to 50+ tenants on one platform',
      'Automated posting across multiple social channels with zero manual scheduling',
    ],
    technologies: ['OpenAI', 'Stable Diffusion', 'Node.js', 'React', 'AWS', 'PostgreSQL'],
  },
  {
    slug: 'sre-observability-automation',
    title: 'End-to-End SRE Observability & Incident Automation',
    category: 'SRE / Observability',
    icon: 'fa-solid fa-gauge-high',
    summary: 'A complete SLA/SLO observability stack with P95/P99 dashboards, ML-driven anomaly detection, and automated ticketing.',
    challenge: 'An enterprise IT team lacked a unified view of service health across dozens of systems and relied on manual dashboards and reactive firefighting, so SLA breaches were often discovered only after customers complained.',
    solution: "We implemented a complete observability stack: Splunk-based dashboards tracking SLIs/SLOs with P95/P99 latency and error-rate metrics, ML/AI models for anomaly detection, Ansible-driven runbooks for automated remediation, and tight integration with their ITSM tool to auto-create and route tickets the moment an issue is detected.",
    results: [
      'Cut mean time to detection (MTTD) from hours to under 5 minutes',
      'Automated ticket creation and routing for the majority of recurring incidents',
      'Gave leadership real-time SLA/SLO compliance visibility through P95/P99 dashboards',
    ],
    technologies: ['Splunk', 'Ansible', 'Python', 'Machine Learning', 'ITSM (ServiceNow)', 'AWS'],
  },
];

function CaseStudyCard({ caseStudy }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <article className={`expertise-card${isExpanded ? ' is-expanded' : ''}`}>
      <div className="expertise-card-icon" aria-hidden="true">
        <i className={caseStudy.icon}></i>
      </div>
      <h6 className="case-study-category"><span>{caseStudy.category}</span></h6>
      <h3>{caseStudy.title}</h3>
      <p className="expertise-card-description">{caseStudy.summary}</p>
      <button
        type="button"
        className="expertise-card-toggle"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        {isExpanded ? 'Hide Case Study' : 'Read Case Study'}
        <i className={`fa-solid ${isExpanded ? 'fa-arrow-up' : 'fa-arrow-right'}`} aria-hidden="true"></i>
      </button>
      {isExpanded && (
        <div className="expertise-card-details">
          <h4>The Challenge</h4>
          <p>{caseStudy.challenge}</p>
          <h4>Our Solution</h4>
          <p>{caseStudy.solution}</p>
          <h4>The Results</h4>
          <ul>
            {caseStudy.results.map((result) => <li key={result}>{result}</li>)}
          </ul>
          <h4>Technology Used</h4>
          <div className="expertise-badges">
            {caseStudy.technologies.map((technology) => <span key={technology}>{technology}</span>)}
          </div>
          <a className="expertise-card-cta" href="/contact">
            Start a Similar Project <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
          </a>
        </div>
      )}
    </article>
  );
}

export default CaseStudyCard;
